import React, { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useInView, useReducedMotion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
interface GallerySlide {
  image: string;
  title: string;
  detail: string;
}
const gallerySlides: GallerySlide[] = [{
  image: 'https://cdn.magicpatterns.com/uploads/5VT1NqhZgqGtjP4aVjGNiM/ginger_5.png',
  title: 'Ondas luminosas',
  detail: 'Dimensión cálida · acabado sedoso'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/nhwrtbBJZJMuSQCDdC3UbR/ginger_4.png',
  title: 'Blonde dimension',
  detail: 'Color personalizado · brillo natural'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/sKZr4sXnX6sG2hMMtrmdgP/ginger_3.png',
  title: 'Recogido editorial',
  detail: 'Textura pulida · detalle artesanal'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/sZ19PCHvfo3WXhSaVDEqYv/ginger_2.png',
  title: 'Balayage miel',
  detail: 'Movimiento suave · luz a medida'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/u6Mj2J8ESKp6kqQNmjqJNN/ginger_1.png',
  title: 'Rubio sofisticado',
  detail: 'Contraste delicado · ondas definidas'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/4PG4st3C5gVkvRZmjmQc9M/ginger_6.png',
  title: 'Ondas caramelo',
  detail: 'Dimensión dorada · movimiento natural'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/wayjZWw6PtQfybgVLnjEmU/GINGER_8.png',
  title: 'Recogido de ocasión',
  detail: 'Trenza suave · detalle joya'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/bq2geH8LgHQUvESer2gVM3/GINGER_7.png',
  title: 'Ponytail plata',
  detail: 'Tono frío · acabado pulido'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/ofHBjwdYYtrcum7VyVjLjX/HIGHLIGHTS.png',
  title: 'Highlights luminosos',
  detail: 'Mechas finas · dimensión natural'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/apek3ibJx8YmYE9cne5fGc/COLOR_CPMLETO.png',
  title: 'Color completo',
  detail: 'Tono uniforme · brillo espejo'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/2tJ8iBsMHyogGpVqxrPhYj/BALAYAGE_GINGER.png',
  title: 'Balayage Ginger',
  detail: 'Iluminación a mano · efecto sol'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/3SJTSdSx9SaKGZX2PTMUuW/ALISADO_GINGER.png',
  title: 'Alisado espejo',
  detail: 'Liso perfecto · máximo brillo'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/uBc4MVVT2bXgohT62sK1Gp/MAQUILLAJE_FULL_FACE.png',
  title: 'Maquillaje full face',
  detail: 'Mirada definida · acabado impecable'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/twiXyvS6NJsTPf3GNjCETw/MAQUILLAJE_GLOW_UP.png',
  title: 'Maquillaje glow up',
  detail: 'Piel luminosa · glow dorado'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/8KVwx7wnDrfDYkaqrmAa53/MONEY_PIECE_FULL_FACE.png',
  title: 'Money piece full face',
  detail: 'Luz al rostro · contraste marcado'
}];
const SLIDE_INTERVAL = 3400;
const formatIndex = (value: number) => String(value).padStart(2, '0');
export function Gallery() {
  const shouldReduceMotion = useReducedMotion();
  const galleryRef = useRef<HTMLElement>(null);
  const isInView = useInView(galleryRef, {
    amount: 0.35
  });
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [isPaused, setIsPaused] = useState(false);
  useEffect(() => {
    if (shouldReduceMotion || !isInView || isPaused) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      setDirection(1);
      setActiveIndex(current => (current + 1) % gallerySlides.length);
    }, SLIDE_INTERVAL);
    return () => window.clearTimeout(timer);
  }, [activeIndex, isInView, isPaused, shouldReduceMotion]);
  const selectSlide = (index: number) => {
    setDirection(index > activeIndex ? 1 : -1);
    setActiveIndex(index);
  };
  const showPrevious = () => {
    setDirection(-1);
    setActiveIndex(current => (current - 1 + gallerySlides.length) % gallerySlides.length);
  };
  const showNext = () => {
    setDirection(1);
    setActiveIndex(current => (current + 1) % gallerySlides.length);
  };
  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      showPrevious();
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      showNext();
    }
  };
  const activeSlide = gallerySlides[activeIndex];
  const slideVariants = {
    enter: (slideDirection: number) => ({
      opacity: 0,
      x: shouldReduceMotion ? 0 : `${slideDirection * 68}%`,
      scale: shouldReduceMotion ? 1 : 1.1
    }),
    center: {
      opacity: 1,
      x: '0%',
      scale: 1
    },
    exit: (slideDirection: number) => ({
      opacity: 0,
      x: shouldReduceMotion ? 0 : `${slideDirection * -34}%`,
      scale: shouldReduceMotion ? 1 : 1.04
    })
  };
  return <motion.section ref={galleryRef} initial={{
    opacity: 0,
    y: shouldReduceMotion ? 0 : 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-80px'
  }} transition={{
    duration: shouldReduceMotion ? 0.1 : 0.65,
    ease: [0.22, 1, 0.36, 1]
  }} aria-roledescription="carousel" aria-label="Galería de transformaciones de cabello" tabIndex={0} onKeyDown={handleKeyDown} onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)} onFocus={() => setIsPaused(true)} onBlur={() => setIsPaused(false)} className="relative my-14 overflow-hidden bg-dark text-warm-white outline-none focus-visible:ring-2 focus-visible:ring-champagne focus-visible:ring-offset-4 focus-visible:ring-offset-warm-white" data-id="element-149" data-mp="1qpdm5pn1jsl1">
      <div className="flex items-center justify-between border-b border-champagne/45 px-6 py-5 sm:px-8" data-id="element-150" data-mp="p0a2v04bioa0">
        <div data-id="element-151" data-mp="t12e1ywr338x">
          <p className="font-body text-[9px] font-semibold uppercase tracking-[4px] text-gold-light sm:text-[10px]" data-id="element-152" data-mp="t2my2ulfv3sj">
            Galería de transformaciones
          </p>
          <h2 className="mt-1 font-serif text-2xl font-light sm:text-3xl" data-id="element-153" data-mp="1rdohfsvtx4p3">
            Resultados que hablan por sí solos.
          </h2>
        </div>
        <span aria-live="polite" className="font-body text-[10px] tracking-[2px] text-cream/60" data-id="element-154" data-mp="21akijdjeeojm">
          {formatIndex(activeIndex + 1)} / {formatIndex(gallerySlides.length)}
        </span>
      </div>

      <div className="relative aspect-[3/4] overflow-hidden bg-[#35291f] sm:aspect-[3/4]" data-id="element-155" data-mp="2ab8ywew9l0zo">
        <AnimatePresence initial={false} custom={direction} data-id="element-156" data-mp="2p05d6towza8q">
          <motion.img key={activeSlide.image} src={activeSlide.image} alt={`${activeSlide.title}: ${activeSlide.detail}`} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={shouldReduceMotion ? {
          duration: 0.1
        } : {
          x: {
            type: 'spring',
            stiffness: 260,
            damping: 28,
            mass: 0.7
          },
          scale: {
            duration: 0.72,
            ease: [0.22, 1, 0.36, 1]
          },
          opacity: {
            duration: 0.32,
            ease: 'easeOut'
          }
        }} className="absolute inset-0 h-full w-full object-cover object-[center_68%]" data-id="element-157" data-mp="2ozlw1f738pvr" />
        </AnimatePresence>

        {!shouldReduceMotion && <div aria-hidden="true" className="absolute inset-x-0 top-0 h-[3px] bg-warm-white/10" data-id="element-158" data-mp="2ynr636njf951">
            <motion.span key={`${activeIndex}-${isPaused}-${isInView}`} className="block h-full origin-left bg-champagne" initial={{
          scaleX: 0
        }} animate={{
          scaleX: isPaused || !isInView ? 0 : 1
        }} transition={{
          duration: SLIDE_INTERVAL / 1000,
          ease: 'linear'
        }} data-id="element-159" data-mp="2jyv0dg8ye87l" />
          </div>}

        <div className="absolute inset-y-0 left-0 flex items-center px-3 sm:px-5" data-id="element-160" data-mp="2fwge0m62sqbk">
          <button type="button" onClick={showPrevious} aria-label="Ver transformación anterior" className="flex h-10 w-10 items-center justify-center border border-warm-white/70 bg-dark/70 text-warm-white transition-colors hover:border-champagne hover:bg-champagne hover:text-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-champagne sm:h-11 sm:w-11" data-id="element-161" data-mp="2ver77iqy1r34">
            <ChevronLeftIcon className="h-5 w-5" strokeWidth={1.5} data-id="element-162" data-mp="2ci82r6r34rcw" />
          </button>
        </div>
        <div className="absolute inset-y-0 right-0 flex items-center px-3 sm:px-5" data-id="element-163" data-mp="27nvmt6othy5w">
          <button type="button" onClick={showNext} aria-label="Ver siguiente transformación" className="flex h-10 w-10 items-center justify-center border border-warm-white/70 bg-dark/70 text-warm-white transition-colors hover:border-champagne hover:bg-champagne hover:text-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-champagne sm:h-11 sm:w-11" data-id="element-164" data-mp="22oizkku1ap2s">
            <ChevronRightIcon className="h-5 w-5" strokeWidth={1.5} data-id="element-165" data-mp="97msehkasrsx" />
          </button>
        </div>
      </div>

      <div className="flex items-end justify-between bg-[#35291f] px-5 py-4 sm:px-7 sm:py-5" data-id="element-166" data-mp="d988ebs4pm7c">
        <div data-id="element-167" data-mp="i92wy8oflzp8">
          <p className="font-serif text-xl sm:text-2xl" data-id="element-168" data-mp="i7i6ov95h2x6">{activeSlide.title}</p>
          <p className="mt-1 font-body text-[9px] uppercase tracking-[2.5px] text-gold-light sm:text-[10px]" data-id="element-169" data-mp="3ho81dycxjkw1">
            {activeSlide.detail}
          </p>
        </div>
        <span aria-hidden="true" className="mb-1 h-px w-12 bg-champagne/70 sm:w-20" data-id="element-170" data-mp="3lmxa3dn0we9c" />
      </div>

      <div className="flex items-center justify-between gap-5 px-6 py-5 sm:px-8" data-id="element-171" data-mp="3qmjnpo6rjwio">
        <div className="flex flex-wrap items-center gap-2" aria-label="Seleccionar imagen de la galería" data-id="element-172" data-mp="3vge9qy9ucnkx">
          {gallerySlides.map((slide, index) => <button key={slide.image} type="button" onClick={() => selectSlide(index)} aria-label={`Ver ${slide.title}`} aria-current={index === activeIndex ? 'true' : undefined} className={`h-1.5 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-champagne focus-visible:ring-offset-2 focus-visible:ring-offset-dark ${index === activeIndex ? 'w-8 bg-champagne' : 'w-2 bg-cream/40 hover:bg-champagne/70'}`} data-id="element-173" data-mp="32y0khn2km9xg" data-mpi={index} />)}
        </div>
        <p className="font-body text-[9px] uppercase tracking-[2px] text-cream/60" data-id="element-174" data-mp="dxxbgdn1rs26">
          Usa flechas o teclado
        </p>
      </div>
    </motion.section>;
}